import { tokens } from '@/design';

export type GraphicBooleanAction = 'union' | 'intersection' | 'xor' | 'difference';
export type GraphicOrderAction = 'forward' | 'backward' | 'front' | 'back';

interface Props {
  readonly count: number;
  readonly canEditVertices: boolean;
  readonly editingVertices: boolean;
  readonly canCombine: boolean;
  readonly onEditVertices: () => void;
  readonly onReverse: () => void;
  readonly onRotate: (degrees: number) => void;
  readonly onFlip: (axis: 'horizontal' | 'vertical') => void;
  readonly onDuplicate: () => void;
  readonly onOrder: (action: GraphicOrderAction) => void;
  readonly onCombine: (action: GraphicBooleanAction) => void;
}

const buttonStyle = {
  minHeight: 24,
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  border: tokens.border.thin,
  background: tokens.color.surface[1],
  color: tokens.color.ink.primary,
  font: 'inherit',
  cursor: 'pointer',
} as const;

export function LayoutGraphicEditToolbar({
  count,
  canEditVertices,
  editingVertices,
  canCombine,
  onEditVertices,
  onReverse,
  onRotate,
  onFlip,
  onDuplicate,
  onOrder,
  onCombine,
}: Props) {
  return (
    <div
      data-testid="layout-graphic-edit-toolbar"
      role="toolbar"
      aria-label={`Graphic editing, ${count} selected`}
      style={{
        position: 'absolute',
        left: tokens.space[3],
        right: tokens.space[3],
        top: 44,
        zIndex: 150,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        padding: 3,
        border: tokens.border.thin,
        borderRadius: tokens.radius.sm,
        background: tokens.color.surface[0],
        boxShadow: tokens.elevation[2],
        fontFamily: tokens.font.family.ui,
        fontSize: 11,
      }}
    >
      <button type="button" style={buttonStyle} disabled={!canEditVertices} aria-pressed={editingVertices} onClick={onEditVertices}>Edit vertices</button>
      <button type="button" style={buttonStyle} disabled={!canEditVertices} onClick={onReverse}>Reverse line direction</button>
      <button type="button" style={buttonStyle} aria-label="Rotate left 90 degrees" onClick={() => onRotate(-90)}>↶ 90°</button>
      <button type="button" style={buttonStyle} aria-label="Rotate right 90 degrees" onClick={() => onRotate(90)}>↷ 90°</button>
      <button type="button" style={buttonStyle} onClick={() => onFlip('horizontal')}>Flip horizontal</button>
      <button type="button" style={buttonStyle} onClick={() => onFlip('vertical')}>Flip vertical</button>
      <button type="button" style={buttonStyle} onClick={onDuplicate}>Duplicate</button>
      <button type="button" style={buttonStyle} onClick={() => onOrder('forward')}>Bring forward</button>
      <button type="button" style={buttonStyle} onClick={() => onOrder('backward')}>Send backward</button>
      <button type="button" style={buttonStyle} onClick={() => onOrder('front')}>Bring to front</button>
      <button type="button" style={buttonStyle} onClick={() => onOrder('back')}>Send to back</button>
      <button type="button" style={buttonStyle} disabled={!canCombine} onClick={() => onCombine('union')}>Union</button>
      <button type="button" style={buttonStyle} disabled={!canCombine} onClick={() => onCombine('intersection')}>Intersection</button>
      <button type="button" style={buttonStyle} disabled={!canCombine} onClick={() => onCombine('xor')}>Remove overlap</button>
      <button type="button" style={buttonStyle} disabled={!canCombine} onClick={() => onCombine('difference')}>Subtract</button>
    </div>
  );
}
